/* PAGINA 6 · PREFERITI
 *
 * I preferiti non sono un elenco: sono la rosa dei candidati, e la schermata è
 * organizzata per DECIDERE. Da qui le tre scelte.
 *
 * 1. Il verdetto apre ogni riga — batte, pari, sotto, perde — perché è la sola
 *    domanda che conta, e accanto al numero c'è sempre il rendimento del
 *    mercato NELLO STESSO PERIODO.
 *
 * 2. Il periodo di misura è su ogni riga e su ogni colonna del confronto.
 *    Qui si confrontano righe diverse fra loro: 2017–2026 e 2022–2026 danno
 *    esiti opposti sulle stesse strategie, e due schede affiancate senza
 *    periodo sono un invito a sbagliare.
 *
 * 3. Il confronto è la funzione principale, non un extra: si scelgono fino a
 *    tre candidati e si guardano affiancati, con l'avvertimento esplicito
 *    quando i periodi non coincidono.
 *
 * IL PESO. `GET /backtester?isFavorite=true` è l'unico modo di leggere i
 * preferiti e risponde con le ENTITY COMPLETE: `trades`,
 * `candlesClosePrices` ed `equityLinePoints`, cioè megabyte per riga, senza
 * limite e senza pagine. Cosa si può fare e cosa no:
 *
 *   - sulla RETE non si può fare niente finché l'endpoint è quello. La
 *     richiesta di `isFavorite` su `/backtester/search` è annotata in
 *     RICHIESTE-API.md ed è già la numero 3 per priorità.
 *   - in MEMORIA sì: la risposta si proietta subito su `PreferitoLeggero`
 *     dentro la funzione di richiesta, così l'array grezzo non entra mai in
 *     uno stato React e il garbage collector se lo riprende.
 *   - a VIDEO sì: si disegnano poche schede per volta, non tutte.
 *   - la richiesta NON si ripete da sola. Nessun ricaricamento in sottofondo
 *     su una chiamata di questo peso: c'è un pulsante, e accanto l'ora della
 *     lettura.
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avvertenza,
  Avvisi,
  AzioneDistruttiva,
  ContenitoreDati,
  DistintivoVerdetto,
  IconaAggiorna,
  IconaSpunta,
  IconaStrategia,
  Intorno,
  NeiRibassi,
  NumeroConSoglia,
  Pulsante,
  ScheletroLista,
  StatoVuoto,
  TernaMetriche,
  Tenuta,
  divisioneDaMetodo,
  usaAvvisi,
} from '../componenti';
import { backtest } from '../api';
import { usaRichiesta } from '../api/usaRichiesta';
import { daQuanto, num, ora, pct, timeframe } from '../lib/formato';
import { aPariRischio, leggiAPariRischio } from '../lib/metriche';
import { Confronto } from './gruppi/Confronto';
import {
  parametriLeggibili,
  periodoDiRiga,
  proiettaPreferito,
  type PreferitoLeggero,
} from './gruppi/dati';
import './gruppi/pagine.css';

/** Quante schede si disegnano per volta. Ogni scheda porta tre blocchi con
 *  soglia: cinquanta insieme sono un elenco che scatta allo scorrimento. */
const PASSO = 6;

/** Oltre tre colonne il confronto smette di stare su un telefono e comincia a
 *  scorrere in orizzontale, che è il modo di non guardarne nessuna. */
const MASSIMO_CONFRONTO = 3;

type ChiaveLocale =
  'scarto' | 'profitto' | 'drawdown' | 'pariRischio' | 'tenuta' | 'ribassi' | 'intorno';

interface CriterioLocale {
  chiave: ChiaveLocale;
  etichetta: string;
  /** Valore su cui ordinare. `null` finisce sempre in fondo. */
  valore: (p: PreferitoLeggero) => number | null;
  /** `-1` dal più alto, `1` dal più basso. */
  verso: -1 | 1;
}

const CRITERI: CriterioLocale[] = [
  {
    chiave: 'scarto',
    etichetta: 'Scarto dal mercato',
    valore: (p) =>
      p.strategyChange == null || p.marketChange == null ? null : p.strategyChange - p.marketChange,
    verso: -1,
  },
  { chiave: 'profitto', etichetta: 'Profitto', valore: (p) => p.strategyChange, verso: -1 },
  {
    chiave: 'drawdown',
    etichetta: 'Drawdown',
    valore: (p) => (p.drawdownMaxValue == null ? null : Math.abs(p.drawdownMaxValue)),
    verso: 1,
  },
  {
    chiave: 'pariRischio',
    etichetta: 'A pari rischio',
    valore: (p) =>
      p.strategyChange == null || p.drawdownMaxValue == null || p.drawdownMaxValue === 0
        ? null
        : aPariRischio(p.strategyChange, p.drawdownMaxValue),
    verso: -1,
  },
  { chiave: 'tenuta', etichetta: 'Tenuta', valore: (p) => p.walkForwardScore, verso: -1 },
  { chiave: 'ribassi', etichetta: 'Nei ribassi', valore: (p) => p.bearYearsAvg, verso: -1 },
  { chiave: 'intorno', etichetta: 'Intorno', valore: (p) => p.intorno, verso: -1 },
];

export function Preferiti() {
  const vaiA = useNavigate();
  const { avvisi, mostra } = usaAvvisi();

  const [chiave, setChiave] = useState<ChiaveLocale>('scarto');
  const [visibili, setVisibili] = useState(PASSO);
  const [scelti, setScelti] = useState<number[]>([]);
  /* I tolti restano segnati qui invece di far ripartire la richiesta: rifare
     una chiamata da megabyte per togliere una riga sarebbe sproporzionato. */
  const [tolti, setTolti] = useState<number[]>([]);

  const richiesta = usaRichiesta<PreferitoLeggero[]>(async (segnale) => {
    /* La funzione di risorsa ora accetta il segnale: questa è la chiamata più
         pesante dell'applicazione e uscendo dalla schermata va annullata. Si
         passava da `api.get` a mano proprio perché il segnale mancava. */
    const completi = await backtest.elencoGrezzo({ isFavorite: true }, segnale);
    /* La proiezione avviene QUI, dentro la funzione: `completi` è una
         variabile locale e muore con essa. Se tornassi l'array intero e
         proiettassi in un `useMemo`, le serie resterebbero vive nello stato
         dell'hook per tutta la vita della schermata. */
    return completi.map(proiettaPreferito);
  }, []);

  /* Rileggere dal server rende obsoleta la contabilità locale dei tolti: le
     righe tolte non tornano indietro, e tenerle segnate falserebbe il
     conteggio dei candidati. */
  const rileggi = useCallback(() => {
    setTolti([]);
    richiesta.ricarica();
  }, [richiesta]);

  const tutti = richiesta.dati ?? [];

  const criterio = CRITERI.find((c) => c.chiave === chiave) ?? CRITERI[0];

  const ordinati = useMemo(() => {
    const vivi = tutti.filter((p) => !tolti.includes(p.id));
    /* Ordinamento LOCALE, e va detto: i preferiti sono già tutti in pagina,
       quindi qui non c'è nessuna query da rifare. Chiamarlo "nuova query sul
       server" come nelle liste da un milione e mezzo di righe sarebbe una
       bugia — e in cambio si può ordinare per "a pari rischio", che il server
       non sa fare. */
    return [...vivi].sort((a, b) => {
      const va = criterio.valore(a);
      const vb = criterio.valore(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      return (va - vb) * criterio.verso;
    });
  }, [tutti, tolti, criterio]);

  const candidati = useMemo(
    () =>
      scelti
        .map((id) => ordinati.find((p) => p.id === id))
        .filter((p): p is PreferitoLeggero => !!p),
    [scelti, ordinati],
  );

  const inverti = useCallback((id: number) => {
    setScelti((s) =>
      s.includes(id) ? s.filter((x) => x !== id) : s.length >= MASSIMO_CONFRONTO ? s : [...s, id],
    );
  }, []);

  const togliDaiPreferiti = useCallback(
    (p: PreferitoLeggero) => {
      /* La risposta di `PATCH .../favorite` è l'entity COMPLETA, un'altra
         volta megabyte: si scarta senza mai assegnarla. La riga sparisce dallo
         stato locale, che è l'unica cosa che serve. */
      backtest
        .segnaPreferito(p.id, false)
        .then(() => {
          setTolti((t) => [...t, p.id]);
          setScelti((s) => s.filter((x) => x !== p.id));
          mostra(`${p.strategyName} tolta dai preferiti`, 'neutro');
        })
        .catch((e: unknown) => {
          mostra(e instanceof Error ? e.message : 'Non è riuscito', 'fallito');
        });
    },
    [mostra],
  );

  const mostrate = ordinati.slice(0, visibili);

  return (
    <div className="sb-cornice">
      <div className="sb-intestazione">
        <div className="sb-intestazione__riga">
          <div style={{ flex: 1, minWidth: 180 }}>
            <h1 className="sb-intestazione__titolo">Preferiti</h1>
            <div className="sb-intestazione__sotto">
              <span className="sb-nota">
                {richiesta.stato === 'pieno'
                  ? `${num(ordinati.length)} candidati · scegline fino a ${MASSIMO_CONFRONTO} da confrontare`
                  : 'la rosa dei candidati, organizzata per decidere'}
              </span>
              {richiesta.letturaA && (
                <span className="sb-nota">
                  letto alle {ora(richiesta.letturaA)} — {daQuanto(richiesta.letturaA)}
                </span>
              )}
            </div>
          </div>
          <Pulsante onClick={rileggi} disabled={richiesta.inAggiornamento}>
            <IconaAggiorna />
            {richiesta.inAggiornamento ? 'Rilettura…' : 'Rileggi'}
          </Pulsante>
        </div>
      </div>

      <div className="sb-pf-elenco">
        {/* Degradazione dichiarata, non nascosta: chi apre questa schermata da
            fuori casa deve sapere perché ci mette. */}
        <Avvertenza livello="cautela">
          I preferiti arrivano da <b>GET /backtester?isFavorite=true</b>, che restituisce i backtest
          interi — operazioni, prezzi e curva di equity comprese — senza pagine e senza limite. Sono
          megabyte per riga: la lettura è una sola, non si ripete da sola, e in memoria resta solo
          quel che serve alla scheda. La richiesta di una versione paginata è annotata in
          RICHIESTE-API.md.
        </Avvertenza>

        <Confronto
          candidati={candidati}
          onTogli={inverti}
          onSvuota={() => setScelti([])}
          onApri={(id) => vaiA(`/backtest/${id}`)}
        />

        {richiesta.stato === 'pieno' && (
          <div className="sb-gr-sezione sb-gr-sezione__imbottita">
            <div
              className="sb-ordinamento__criteri"
              role="group"
              aria-label="Criterio di ordinamento"
            >
              {CRITERI.map((c) => {
                const on = c.chiave === chiave;
                return (
                  <button
                    key={c.chiave}
                    type="button"
                    className={`sb-criterio${on ? ' sb-criterio--attivo' : ''}`}
                    aria-pressed={on}
                    onClick={() => setChiave(c.chiave)}
                  >
                    {c.etichetta}
                    {on && <span className="sb-criterio__verso">{c.verso === -1 ? '↓' : '↑'}</span>}
                  </button>
                );
              })}
            </div>
            <div className="sb-ordinamento__nota" style={{ marginTop: 'var(--sb-s-4)' }}>
              riordino locale su {num(ordinati.length)} preferiti già in pagina — nessuna query. È
              il motivo per cui qui «a pari rischio» si può ordinare e nelle liste da un milione e
              mezzo di righe no.
            </div>
          </div>
        )}

        <ContenitoreDati
          stato={richiesta.stato}
          causaVuoto="Nessun backtest è stato ancora messo fra i preferiti. Si aggiungono dal dettaglio di un backtest."
          messaggioErrore={richiesta.errore?.message}
          onRiprova={rileggi}
          scheletro={<ScheletroLista righe={3} />}
        >
          {/* Togliere l'ultimo candidato lascia la lista vuota mentre la
              richiesta resta "piena": senza questo ramo si vedrebbe una
              pagina bianca senza spiegazione. */}
          {ordinati.length === 0 && (
            <StatoVuoto
              titolo="Rosa vuota"
              causa="Tutti i preferiti sono stati tolti in questa sessione. Se ne aggiungono dal dettaglio di un backtest."
              azione={{ etichetta: 'Rileggi dal server', onClick: rileggi }}
            />
          )}

          {mostrate.map((p) => (
            <SchedaPreferito
              key={p.id}
              preferito={p}
              scelto={scelti.includes(p.id)}
              /* Il quarto tocco non fa niente: meglio un controllo spento con
                 il limite scritto sotto che un candidato che sparisce a
                 sorpresa per far posto. */
              selezionabile={scelti.includes(p.id) || scelti.length < MASSIMO_CONFRONTO}
              onScegli={() => inverti(p.id)}
              onApri={() => vaiA(`/backtest/${p.id}`)}
              onTogli={() => togliDaiPreferiti(p)}
            />
          ))}

          {ordinati.length > mostrate.length && (
            <Pulsante variante="tenue" largo onClick={() => setVisibili((v) => v + PASSO)}>
              Mostra altri {num(Math.min(PASSO, ordinati.length - mostrate.length))} ·{' '}
              {num(ordinati.length - mostrate.length)} ancora da vedere
            </Pulsante>
          )}
        </ContenitoreDati>
      </div>

      <Avvisi avvisi={avvisi} />
    </div>
  );
}

/* ==========================================================================
 * LA SCHEDA
 * ======================================================================== */

function SchedaPreferito({
  preferito: p,
  scelto,
  selezionabile,
  onScegli,
  onApri,
  onTogli,
}: {
  preferito: PreferitoLeggero;
  scelto: boolean;
  selezionabile: boolean;
  onScegli: () => void;
  onApri: () => void;
  onTogli: () => void;
}) {
  const periodo = periodoDiRiga(p);

  return (
    <div className={`sb-pf-scheda${scelto ? ' sb-pf-scheda--scelta' : ''}`}>
      <div className="sb-pf-scheda__testata">
        <button
          type="button"
          role="checkbox"
          aria-checked={scelto}
          aria-label={`Confronta ${p.strategyName}`}
          disabled={!selezionabile}
          className={`sb-pf-scelta${scelto ? ' sb-pf-scelta--attiva' : ''}`}
          onClick={onScegli}
        >
          <span style={{ opacity: scelto ? 1 : 0.25, display: 'flex' }}>
            <IconaSpunta dim={15} />
          </span>
        </button>

        {/* Il verdetto apre la riga: è la prima cosa che si legge. */}
        <DistintivoVerdetto profitto={p.strategyChange} mercato={p.marketChange} />

        <IconaStrategia nome={p.strategyName} dim={26} />

        <button type="button" className="sb-pf-scheda__apri sb-pf-scheda__corpo" onClick={onApri}>
          <span className="sb-pf-scheda__nome">{p.strategyName}</span>
          <span className="sb-pf-scheda__sotto">
            {p.asset}/{p.currency} · {timeframe(p.candleSize)}
            {p.backtestGroupId != null ? ` · gruppo #${num(p.backtestGroupId)}` : ' · singolo'}
          </span>
        </button>

      </div>

      {p.description && <div className="sb-nota">{p.description}</div>}

      <TernaMetriche
        densita="riquadri"
        profitto={p.strategyChange}
        drawdown={p.drawdownMaxValue}
        operazioni={p.tradesNumber}
        periodo={periodo}
      />

      {/* Il profitto senza il mercato dello stesso periodo non è un verdetto:
          sta subito sotto la terna, non in fondo. */}
      <div className="sb-pf-candidato__riga">
        <span>Mercato su {periodo}</span>
        <b className="sb-tono-neutro">{p.marketChange == null ? 'n/d' : pct(p.marketChange)}</b>
      </div>

      <div className="sb-pf-soglie">
        <Tenuta
          valore={p.walkForwardScore}
          divisione={divisioneDaMetodo(p.walkForwardMetodo)}
          periodo={periodo}
        />
        <NeiRibassi valore={p.bearYearsAvg} anniNegativi={p.bearYearsCount} periodo={periodo} />
        <NumeroConSoglia
          titolo="A pari rischio"
          lettura={leggiAPariRischio(p.strategyChange, p.drawdownMaxValue)}
          periodo={periodo}
        />
        {/* L'intorno esiste solo dentro una griglia: su un backtest singolo si
            mostra comunque, con scritto perché è n/d. */}
        <Intorno
          valore={p.intorno}
          parametroPiuFragile={p.intornoParametro}
          assi={p.intornoAssi}
          periodo={periodo}
        />
      </div>

      <div className="sb-nota">{parametriLeggibili(p.strategyParams)}</div>

      {/* In fondo alla scheda e a due tocchi: è l'unica azione che toglie un
          candidato dalla rosa, e in tasca si preme da sola. */}
      <AzioneDistruttiva
        etichetta="Togli dai preferiti"
        etichettaArmata="Tocca di nuovo per toglierlo"
        nota="il backtest resta, esce solo dalla rosa dei candidati"
        onConferma={onTogli}
      />
    </div>
  );
}
